import { App } from '@capacitor/app';
import { isNative, isAndroid } from './platform';

/**
 * Bouton retour matériel d'Android.
 *
 * Sans ce gestionnaire, Capacitor applique son comportement par défaut : le
 * bouton retour ferme l'application, quel que soit l'écran affiché. Un
 * utilisateur qui consulte une fiche d'offre et appuie sur retour pour revenir
 * à la liste se retrouve sorti de l'app. C'est le reproche le plus courant
 * fait aux applications web encapsulées sur Android.
 *
 * Comportement installé ici :
 *   - une couche superposée est ouverte  → on la ferme ;
 *   - il existe une page précédente      → on y revient ;
 *   - on est sur un écran racine         → double appui pour quitter.
 *
 * Le double appui évite la sortie accidentelle : le premier appui affiche un
 * message, le second dans les deux secondes quitte réellement.
 */

const ROOT_ROUTES = ['HomePage', 'OffersList', 'Gallery', 'AccountPage', 'LoginPage'];

const EXIT_CONFIRM_DELAY = 2000;

let listenerHandle = null;
let exitArmedAt = 0;
let toastEl = null;

function showExitHint() {
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'oc-toast';
    toastEl.textContent = 'Appuyez à nouveau pour quitter';
    document.body.appendChild(toastEl);
  }
  toastEl.classList.add('is-visible');
  window.setTimeout(() => {
    if (toastEl) toastEl.classList.remove('is-visible');
  }, EXIT_CONFIRM_DELAY);
}

/**
 * Les couches superposées doivent se fermer avant toute navigation. Un élément
 * qui veut intercepter le retour se signale par l'attribut [data-oc-dismissable]
 * et expose un bouton de fermeture [data-oc-dismiss].
 */
function closeTopOverlay() {
  const overlays = document.querySelectorAll('[data-oc-dismissable]');
  if (!overlays.length) return false;

  const top = overlays[overlays.length - 1];
  const closer = top.querySelector('[data-oc-dismiss]');
  if (closer) {
    closer.click();
  } else {
    top.dispatchEvent(new CustomEvent('oc-dismiss', { bubbles: true }));
  }
  return true;
}

export async function initBackButton(router) {
  // Le bouton retour matériel n'existe que sur Android ; iOS utilise le geste
  // de balayage, géré nativement par la webview.
  if (!isNative() || !isAndroid()) return;

  if (listenerHandle) {
    await listenerHandle.remove();
    listenerHandle = null;
  }

  listenerHandle = await App.addListener('backButton', () => {
    if (closeTopOverlay()) return;

    const current = router.currentRoute.value;
    const onRootScreen = ROOT_ROUTES.includes(current.name);

    if (!onRootScreen && window.history.state && window.history.state.back) {
      router.back();
      return;
    }

    // Depuis un écran non racine ouvert directement (lien profond, notification
    // push), il n'y a pas d'historique : on ramène à l'accueil plutôt que de
    // quitter, sinon le lien profond devient un cul-de-sac.
    if (!onRootScreen) {
      router.replace({ name: 'HomePage' });
      return;
    }

    // Depuis un onglet secondaire, on revient d'abord à l'accueil.
    if (current.name !== 'HomePage') {
      router.replace({ name: 'HomePage' });
      return;
    }

    const now = Date.now();
    if (now - exitArmedAt < EXIT_CONFIRM_DELAY) {
      App.exitApp();
    } else {
      exitArmedAt = now;
      showExitHint();
    }
  });
}
